import React, { useCallback, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import * as Sentry from "@sentry/react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import { BsChatDots, BsGraphUpArrow, BsGraphDownArrow, BsActivity, BsBarChart, BsExclamationTriangle, BsClock } from "react-icons/bs";
import analyticsDashboardApi from "../../../../services/analyticsDashboard.service";
import { formatNumber, formatDate } from "../../../../utils/analyticsDashboardFormat";
import {
  WidgetSection,
  MetricsGrid,
  MetricCard,
  MetricCardHeader,
  MetricLabel,
  MetricIconWrap,
  MetricValue,
  MetricTrend,
  ChartsGrid,
  ChartCard,
  ChartCardTitle,
  SkeletonBlock,
  EmptyStateBox,
  InfoNote,
  MetricTooltip,
} from "./Widgets.styles";

// UTC offset in whole hours for the browser's current timezone.
// Computed once at module load; stable for the tab's lifetime.
const LOCAL_UTC_OFFSET_H = -new Date().getTimezoneOffset() / 60;
// Minutes variant used for backend timezoneOffset param (minutes = hours * 60).
const LOCAL_UTC_OFFSET_MIN = -new Date().getTimezoneOffset();

const MessageAnalyticsWidgets = ({ companyId, startDate, endDate, channelIds, getChannelName }) => {
  // NOTE: `/analytics/clickhouse/message-analytics` only accepts a single
  // `channelId` today (backend limitation, tracked as a follow-up in PR
  // #168). `getChannelPerformance` (below) does support an array of
  // `channelIds`, but we still pass it the same single channel here so
  // both queries stay consistent with each other.
  const channelId = channelIds?.[0];
  const hasMultipleChannelsSelected = (channelIds?.length || 0) > 1;
  const resolveChannelName = useCallback(
    (id) => (getChannelName ? getChannelName(id) : id),
    [getChannelName]
  );

  const {
    data: messageAnalytics,
    isLoading: messageLoading,
    error: messageError,
  } = useQuery(
    ["clickhouse-message-analytics", companyId, startDate, endDate, channelId],
    () =>
      analyticsDashboardApi.getClickHouseMessageAnalytics({
        companyId,
        startDate,
        endDate,
        channelId,
        timezoneOffset: LOCAL_UTC_OFFSET_MIN,
        limit: 10000,
      }),
    { enabled: !!companyId }
  );

  const {
    data: channelPerformance,
    isLoading: channelLoading,
    error: channelError,
  } = useQuery(
    ["clickhouse-channel-performance", companyId, startDate, endDate, channelId],
    () =>
      analyticsDashboardApi.getChannelPerformance({
        companyId,
        startDate,
        endDate,
        channelIds: channelId ? [channelId] : undefined,
        timezoneOffset: LOCAL_UTC_OFFSET_MIN,
      }),
    { enabled: !!companyId }
  );

  // Hourly distribution — independent query; errors are non-fatal for the widget
  const {
    data: messageByHour,
    isLoading: hourLoading,
  } = useQuery(
    ["clickhouse-message-analytics-by-hour", companyId, startDate, endDate, channelId],
    () =>
      analyticsDashboardApi.getClickHouseMessageAnalyticsByHour({
        companyId,
        startDate,
        endDate,
        channelId,
        timezoneOffset: LOCAL_UTC_OFFSET_MIN,
      }),
    { enabled: !!companyId }
  );

  const isLoading = messageLoading || channelLoading;
  const error = messageError || channelError;

  useEffect(() => {
    if (error) {
      Sentry.captureException(error, {
        tags: { component: "MessageAnalyticsWidgets", op: "fetchMessageAnalytics" },
      });
    }
  }, [error]);

  const messageTotals = useMemo(() => {
    if (!messageAnalytics?.data) return null;
    return messageAnalytics.data.reduce(
      (acc, item) => ({
        totalMessages: acc.totalMessages + (item.total_messages || 0),
        incomingMessages: acc.incomingMessages + (item.incoming_messages || 0),
        outgoingMessages: acc.outgoingMessages + (item.outgoing_messages || 0),
      }),
      { totalMessages: 0, incomingMessages: 0, outgoingMessages: 0 }
    );
  }, [messageAnalytics]);

  const timeSeriesData = useMemo(() => {
    if (!messageAnalytics?.data) return [];
    const dataMap = new Map();
    messageAnalytics.data.forEach((item) => {
      const date = item.period_start ? item.period_start.split(" ")[0] : "Unknown";
      if (!dataMap.has(date)) dataMap.set(date, { date, incoming: 0, outgoing: 0, total: 0 });
      const existing = dataMap.get(date);
      existing.incoming += item.incoming_messages || 0;
      existing.outgoing += item.outgoing_messages || 0;
      existing.total += item.total_messages || 0;
    });
    return Array.from(dataMap.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [messageAnalytics]);

  const channelData = useMemo(() => {
    if (!channelPerformance?.data) return [];
    return channelPerformance.data
      .map((item) => ({
        channelId: item.channel_id || "Unknown",
        channel: resolveChannelName(item.channel_id) || item.channel_id || "Unknown",
        total: item.total_messages || 0,
        incoming: item.incoming_messages || 0,
        outgoing: item.outgoing_messages || 0,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [channelPerformance, resolveChannelName]);

  const trends = useMemo(() => {
    if (timeSeriesData.length < 2) return { totalTrend: 0, incomingTrend: 0, outgoingTrend: 0 };
    const avgOf = (items, key) => items.reduce((sum, item) => sum + item[key], 0) / items.length;
    const pctChange = (recent, previous) => (previous > 0 ? ((recent - previous) / previous) * 100 : 0);
    const recentWindow = timeSeriesData.slice(-3);
    const previousWindow = timeSeriesData.slice(-6, -3);
    return {
      totalTrend: pctChange(avgOf(recentWindow, "total"), avgOf(previousWindow, "total")),
      incomingTrend: pctChange(avgOf(recentWindow, "incoming"), avgOf(previousWindow, "incoming")),
      outgoingTrend: pctChange(avgOf(recentWindow, "outgoing"), avgOf(previousWindow, "outgoing")),
    };
  }, [timeSeriesData]);

  /**
   * Hourly distribution shifted from UTC to the viewer's local timezone.
   *
   * The backend returns rows for hour_utc ∈ [0, 23]. We shift each UTC hour by
   * the browser's UTC offset (in whole hours) so the chart labels reflect local
   * time. Hours are modulo-wrapped so they stay in [0, 23].
   *
   * Because the data is already aggregated (sum over the entire date range),
   * the shift is purely presentational — we are NOT rebucketing raw events.
   *
   * LOCAL_UTC_OFFSET_H is stable for the lifetime of the tab session
   * (timezone changes require a page reload anyway).
   */
  const hourlyData = useMemo(() => {
    if (!messageByHour?.data?.length) return [];
    // Build a 24-slot array initialised to zero so gaps show as 0, not missing
    const slots = Array.from({ length: 24 }, (_, h) => ({
      hour: h,
      label: `${String(h).padStart(2, "0")}:00`,
      total: 0,
      incoming: 0,
      outgoing: 0,
    }));
    messageByHour.data.forEach((row) => {
      const utcH = Number(row.hour_utc);
      const localH = ((utcH + LOCAL_UTC_OFFSET_H) % 24 + 24) % 24; // wrap negative
      slots[localH].total    += Number(row.total_messages    || 0);
      slots[localH].incoming += Number(row.incoming_messages || 0);
      slots[localH].outgoing += Number(row.outgoing_messages || 0);
    });
    return slots;
  }, [messageByHour, LOCAL_UTC_OFFSET_H]);

  if (isLoading) {
    return (
      <MetricsGrid columns={3}>
        {[...Array(3)].map((_, i) => (
          <MetricCard key={i}>
            <SkeletonBlock style={{ height: 16, width: "50%", marginBottom: 12 }} />
            <SkeletonBlock style={{ height: 48 }} />
          </MetricCard>
        ))}
      </MetricsGrid>
    );
  }

  if (error) {
    return (
      <EmptyStateBox>
        <BsExclamationTriangle size={32} style={{ color: "#DC2626", marginBottom: 12 }} />
        <div>Error al cargar analíticas de mensajes</div>
      </EmptyStateBox>
    );
  }

  const renderTrend = (value) => (
    <MetricTrend negative={value < 0}>
      {value >= 0 ? <BsGraphUpArrow /> : <BsGraphDownArrow />} {Math.abs(value).toFixed(1)}% vs período anterior
    </MetricTrend>
  );

  // Modern chart color palette
  const CHART_COLORS = {
    incoming: "#10b981",
    outgoing: "#6366f1",
    total: "#3b82f6",
  };

  const tooltipStyle = {
    contentStyle: {
      background: "white",
      border: "1px solid #E5E7EB",
      borderRadius: "12px",
      boxShadow: "0 6px 18px rgba(15,23,42,0.10)",
      fontFamily: "'Inter', sans-serif",
      fontSize: "0.82rem",
    },
    labelStyle: { fontWeight: 700, color: "#111827", marginBottom: 4 },
    itemStyle: { color: "#4B5563" },
    cursor: { stroke: "#E5E7EB", strokeWidth: 1 },
  };

  return (
    <WidgetSection>
      {hasMultipleChannelsSelected && (
        <InfoNote>
          Hay {channelIds.length} canales seleccionados, pero el análisis de mensajes solo admite un canal a la vez.
          Mostrando canal <strong>{resolveChannelName(channelId)}</strong>.
        </InfoNote>
      )}
      <MetricsGrid columns={3}>
        <MetricCard accentColor="#3b82f6">
          <MetricCardHeader>
            <MetricLabel>
              Total de Mensajes
              <MetricTooltip text="Suma de todos los mensajes (entrantes y salientes) procesados en el período seleccionado." />
            </MetricLabel>
            <MetricIconWrap bg="#EFF6FF" color="#2563EB"><BsChatDots /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatNumber(messageTotals?.totalMessages || 0)}</MetricValue>
          {renderTrend(trends.totalTrend)}
        </MetricCard>
        <MetricCard accentColor="#10b981">
          <MetricCardHeader>
            <MetricLabel>
              Mensajes Entrantes
              <MetricTooltip text="Mensajes recibidos de los contactos hacia el canal. Indica el volumen de demanda entrante." />
            </MetricLabel>
            <MetricIconWrap bg="#F0FDF4" color="#16A34A"><BsGraphDownArrow /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatNumber(messageTotals?.incomingMessages || 0)}</MetricValue>
          {renderTrend(trends.incomingTrend)}
        </MetricCard>
        <MetricCard accentColor="#6366f1">
          <MetricCardHeader>
            <MetricLabel>
              Mensajes Salientes
              <MetricTooltip text="Mensajes enviados por agentes o bots hacia los contactos. Refleja la capacidad de respuesta del equipo." />
            </MetricLabel>
            <MetricIconWrap bg="#EEF2FF" color="#6366f1"><BsGraphUpArrow /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatNumber(messageTotals?.outgoingMessages || 0)}</MetricValue>
          {renderTrend(trends.outgoingTrend)}
        </MetricCard>
      </MetricsGrid>

      <ChartsGrid columns={2}>
        <ChartCard>
          <ChartCardTitle>
            <BsActivity /> Volumen de Mensajes en el Tiempo
            <MetricTooltip text="Evolución diaria de mensajes entrantes y salientes en el período. Útil para detectar picos de actividad." />
          </ChartCardTitle>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={timeSeriesData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gradIncoming" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS.incoming} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={CHART_COLORS.incoming} stopOpacity={0.03} />
                </linearGradient>
                <linearGradient id="gradOutgoing" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS.outgoing} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={CHART_COLORS.outgoing} stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="date" tickFormatter={(value) => formatDate(value)} tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
              <Tooltip labelFormatter={(value) => formatDate(value)} formatter={(value, name) => [formatNumber(value), name]} {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: "0.8rem", paddingTop: 8 }} />
              <Area type="monotone" dataKey="incoming" stroke={CHART_COLORS.incoming} strokeWidth={2} fill="url(#gradIncoming)" name="Entrantes" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
              <Area type="monotone" dataKey="outgoing" stroke={CHART_COLORS.outgoing} strokeWidth={2} fill="url(#gradOutgoing)" name="Salientes" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard>
          <ChartCardTitle>
            <BsBarChart /> Distribución de Mensajes
            <MetricTooltip text="Proporción de mensajes entrantes y salientes por cada canal activo en el período. Permite identificar los canales con mayor carga." />
          </ChartCardTitle>
          <ResponsiveContainer width="100%" height={300}>
            {(() => {
              // Build a stable, flat array of pie rows so that <Pie data> and
              // its <Cell> children share the exact same reference and indices.
              const pieRows = [];
              channelData.forEach((ch) => {
                if (ch.incoming > 0)
                  pieRows.push({ name: `${ch.channel} – Entrantes`, value: Number(ch.incoming), fill: CHART_COLORS.incoming });
                if (ch.outgoing > 0)
                  pieRows.push({ name: `${ch.channel} – Salientes`, value: Number(ch.outgoing), fill: CHART_COLORS.outgoing });
              });
              return (
                <PieChart width={400} height={300}>
                  <Pie
                    data={pieRows}
                    cx="50%"
                    cy="50%"
                    innerRadius={65}
                    outerRadius={110}
                    paddingAngle={3}
                    dataKey="value"
                    nameKey="name"
                    label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ""}
                    labelLine={false}
                  >
                    {pieRows.map((row, idx) => (
                      <Cell key={`cell-${idx}`} fill={row.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [formatNumber(value), "Mensajes"]} {...tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: "0.8rem", paddingTop: 8 }} />
                </PieChart>
              );
            })()}
          </ResponsiveContainer>
        </ChartCard>
      </ChartsGrid>

      <ChartCard>
        <ChartCardTitle>
          <BsGraphUpArrow /> Tendencias Diarias de Mensajes
          <MetricTooltip text="Serie temporal con el total, entrantes y salientes día a día. La comparación de las tres líneas revela el equilibrio entre demanda y respuesta." />
        </ChartCardTitle>
        <ResponsiveContainer width="100%" height={380}>
          <LineChart data={timeSeriesData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
            <XAxis dataKey="date" tickFormatter={(value) => formatDate(value)} tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
            <Tooltip labelFormatter={(value) => formatDate(value)} formatter={(value, name) => [formatNumber(value), name]} {...tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: "0.8rem", paddingTop: 8 }} />
            <Line type="monotone" dataKey="total" stroke={CHART_COLORS.total} strokeWidth={2.5} name="Total de Mensajes" dot={false} activeDot={{ r: 6, strokeWidth: 0 }} />
            <Line type="monotone" dataKey="incoming" stroke={CHART_COLORS.incoming} strokeWidth={2} name="Entrantes" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} strokeDasharray="0" />
            <Line type="monotone" dataKey="outgoing" stroke={CHART_COLORS.outgoing} strokeWidth={2} name="Salientes" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* ── Volumen de Mensajes por Hora ─────────────────────────────────── */}
      <ChartCard>
        <ChartCardTitle>
          <BsClock /> Volumen de Mensajes por Hora del Día
          <MetricTooltip text={`Distribución del volumen de mensajes (entrantes y salientes) agrupada por hora del día en horario local (UTC${LOCAL_UTC_OFFSET_H >= 0 ? "+" : ""}${LOCAL_UTC_OFFSET_H}). Identifica los picos de actividad para optimizar la disponibilidad del equipo.`} />
        </ChartCardTitle>
        {hourLoading ? (
          <SkeletonBlock style={{ height: 320, borderRadius: 8 }} />
        ) : hourlyData.length === 0 ? (
          <EmptyStateBox style={{ padding: "32px 0" }}>
            <BsExclamationTriangle size={24} style={{ color: "#9CA3AF", marginBottom: 8 }} />
            <div style={{ fontSize: "0.85rem", color: "#6B7280" }}>Sin datos de distribución horaria para el período seleccionado.</div>
          </EmptyStateBox>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={hourlyData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }} barSize={12}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: "#9CA3AF" }}
                axisLine={false}
                tickLine={false}
                interval={1}
              />
              <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
              <Tooltip
                formatter={(value, name) => [formatNumber(value), name]}
                labelFormatter={(label) => `Hora: ${label}`}
                {...tooltipStyle}
              />
              <Legend wrapperStyle={{ fontSize: "0.8rem", paddingTop: 8 }} />
              <Bar dataKey="incoming" name="Entrantes" fill={CHART_COLORS.incoming} radius={[3, 3, 0, 0]} />
              <Bar dataKey="outgoing" name="Salientes" fill={CHART_COLORS.outgoing} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </WidgetSection>
  );
};

export default MessageAnalyticsWidgets;
